import { useEffect, useMemo, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { useMustKnowLaws, useLawAwarenessSetting, type MustKnowLaw } from '@/hooks/use-supabase-data'
import {
  Scale, Plus, Edit, Trash2, Save, RefreshCw, AlertCircle, CheckCircle2, Eye, EyeOff, Loader2,
} from 'lucide-react'

interface LawFormState {
  title: string
  summary: string
  icon: string
  display_order: string
  bns_section: string
  rights_title: string
  rights_points: string
  proof_documents: string
  punishment_title: string
  punishment_points: string
  support_title: string
  support_points: string
  tip: string
}

const defaultForm: LawFormState = {
  title: '',
  summary: '',
  icon: 'shield-check',
  display_order: '',
  bns_section: '',
  rights_title: 'Your Rights',
  rights_points: '',
  proof_documents: '',
  punishment_title: 'If The Law Is Broken',
  punishment_points: '',
  support_title: 'Where to Complain / Get Help',
  support_points: '',
  tip: '',
}

const iconOptions = [
  { value: 'shield-check', label: 'Shield Check' },
  { value: 'gavel', label: 'Gavel' },
  { value: 'scale', label: 'Scale' },
  { value: 'alert-triangle', label: 'Alert Triangle' },
  { value: 'user-check', label: 'User Check' },
] as const

const splitPoints = (text: string) =>
  text.split('\n').map((l) => l.trim()).filter((l) => l.length > 0)

const joinPoints = (points: string[] | null | undefined) =>
  Array.isArray(points) ? points.join('\n') : ''

export function LawAwarenessManager() {
  const { laws, loading: lawsLoading, error: lawsError, refetch } = useMustKnowLaws()
  const { isEnabled, loading: settingLoading, setEnabled } = useLawAwarenessSetting()

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingLaw, setEditingLaw] = useState<MustKnowLaw | null>(null)
  const [form, setForm] = useState<LawFormState>(defaultForm)
  const [saving, setSaving] = useState(false)
  const [toggling, setToggling] = useState(false)
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  useEffect(() => {
    if (!isDialogOpen) {
      setForm(defaultForm)
      setEditingLaw(null)
    }
  }, [isDialogOpen])

  const orderedLaws = useMemo(
    () => laws.slice().sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999)),
    [laws],
  )

  const showStatus = (type: 'success' | 'error', message: string) => {
    setStatus({ type, message })
    setTimeout(() => setStatus(null), 4000)
  }

  const handleToggleSection = async (checked: boolean) => {
    setToggling(true)
    const ok = await setEnabled(checked)
    setToggling(false)
    if (ok) {
      showStatus('success', checked ? 'Law Awareness section is now visible on the Awareness page.' : 'Law Awareness section is now hidden.')
    } else {
      showStatus('error', 'Failed to update section visibility. Please try again.')
    }
  }

  const handleEdit = (law: MustKnowLaw) => {
    setEditingLaw(law)
    setForm({
      title: law.title ?? '',
      summary: law.summary ?? '',
      icon: law.icon ?? 'shield-check',
      display_order: law.display_order?.toString() ?? '',
      bns_section: law.bns_section ?? '',
      rights_title: law.rights_title ?? 'Your Rights',
      rights_points: joinPoints(law.rights_points),
      proof_documents: joinPoints(law.proof_documents),
      punishment_title: law.punishment_title ?? 'If The Law Is Broken',
      punishment_points: joinPoints(law.punishment_points),
      support_title: law.support_title ?? 'Where to Complain / Get Help',
      support_points: joinPoints(law.support_points),
      tip: law.tip ?? '',
    })
    setIsDialogOpen(true)
  }

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase.from('must_know_laws').delete().eq('id', id)
      if (error) throw error
      showStatus('success', 'Law entry deleted.')
      await refetch()
    } catch {
      showStatus('error', 'Failed to delete. Please try again.')
    }
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.title.trim()) { showStatus('error', 'Title is required.'); return }
    if (!form.summary.trim()) { showStatus('error', 'Summary is required.'); return }

    setSaving(true)
    const payload = {
      title: form.title.trim(),
      summary: form.summary.trim(),
      icon: form.icon,
      display_order: form.display_order.trim() ? parseInt(form.display_order.trim(), 10) : null,
      bns_section: form.bns_section.trim() || null,
      rights_title: form.rights_title.trim() || 'Your Rights',
      rights_points: splitPoints(form.rights_points),
      proof_documents: splitPoints(form.proof_documents),
      punishment_title: form.punishment_title.trim() || 'If The Law Is Broken',
      punishment_points: splitPoints(form.punishment_points),
      support_title: form.support_title.trim() || 'Where to Complain / Get Help',
      support_points: splitPoints(form.support_points),
      tip: form.tip.trim() || null,
      updated_at: new Date().toISOString(),
    }

    try {
      if (editingLaw?.id) {
        const { error } = await supabase.from('must_know_laws').update(payload).eq('id', editingLaw.id)
        if (error) throw error
      } else {
        const { error } = await supabase.from('must_know_laws').insert({ ...payload, created_at: new Date().toISOString() })
        if (error) throw error
      }
      setIsDialogOpen(false)
      showStatus('success', 'Law entry saved successfully.')
      await refetch()
    } catch {
      showStatus('error', 'Failed to save. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const field = (key: keyof LawFormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => setForm((prev) => ({ ...prev, [key]: e.target.value }))

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-1">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-amber-600 to-orange-500 bg-clip-text text-transparent">
            Law Awareness
          </h2>
          <p className="text-slate-600 text-lg">
            Manage legal rights information shown in the Awareness page.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => refetch()} disabled={lawsLoading} className="hover:bg-amber-50 hover:border-amber-200">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={() => { setEditingLaw(null); setForm(defaultForm); setIsDialogOpen(true) }}
            className="bg-gradient-to-r from-amber-600 to-orange-500 hover:from-amber-700 hover:to-orange-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Law
          </Button>
        </div>
      </div>

      {/* Status alert */}
      {status && (
        <Alert
          variant={status.type === 'error' ? 'destructive' : 'default'}
          className={status.type === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : undefined}
        >
          {status.type === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          <AlertDescription>{status.message}</AlertDescription>
        </Alert>
      )}

      {/* Section visibility toggle */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {isEnabled ? <Eye className="w-5 h-5 text-emerald-500" /> : <EyeOff className="w-5 h-5 text-slate-400" />}
            Section Visibility
          </CardTitle>
          <CardDescription>
            Control whether the Law Awareness section appears on the public Awareness page.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            {settingLoading ? (
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            ) : (
              <>
                <Switch
                  id="law-awareness-toggle"
                  checked={isEnabled}
                  onCheckedChange={handleToggleSection}
                  disabled={toggling}
                />
                <Label htmlFor="law-awareness-toggle" className="cursor-pointer">
                  {isEnabled ? (
                    <span className="text-emerald-600 font-medium">Visible — shown on Awareness page</span>
                  ) : (
                    <span className="text-slate-500">Hidden — not shown on Awareness page</span>
                  )}
                </Label>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Laws list */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Scale className="w-5 h-5" />
            Law Entries ({laws.length})
          </CardTitle>
          <CardDescription>
            Each entry appears as a card showing rights, consequences, and where to complain.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {lawsError && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{lawsError}</AlertDescription>
            </Alert>
          )}

          {lawsLoading ? (
            <div className="py-10 text-center text-muted-foreground">Loading law entries...</div>
          ) : orderedLaws.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              No law entries yet. Click "Add Law" to create the first one.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Summary</TableHead>
                  <TableHead>Icon</TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orderedLaws.map((law) => (
                  <TableRow key={law.id}>
                    <TableCell className="font-medium">{law.title}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{law.summary}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">{law.icon}</Badge>
                    </TableCell>
                    <TableCell>{law.display_order ?? '—'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleEdit(law)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this law entry?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will remove "{law.title}" from the Awareness page. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-gradient-to-r from-rose-600 to-orange-500"
                                onClick={() => handleDelete(law.id)}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingLaw ? 'Edit Law Entry' : 'Add Law Entry'}</DialogTitle>
            <DialogDescription>
              Fill in the law details. Each section (Rights, Consequences, Where to Complain) takes one item per line.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSave} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="law-title">Title *</Label>
                <Input id="law-title" value={form.title} onChange={field('title')}
                  placeholder="e.g. Police Cannot Beat You" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="law-icon">Icon</Label>
                <select id="law-icon" value={form.icon} onChange={field('icon')}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  {iconOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="law-summary">Short Summary *</Label>
              <Textarea id="law-summary" rows={2} value={form.summary} onChange={field('summary')}
                placeholder="Brief description of this law and why it matters to villagers." />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="law-order">Display Order (optional)</Label>
                <Input id="law-order" type="number" min={0} value={form.display_order}
                  onChange={field('display_order')} placeholder="e.g. 1" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bns-section">BNS / Legal Reference (optional)</Label>
                <Input id="bns-section" value={form.bns_section} onChange={field('bns_section')}
                  placeholder="e.g. Section 117(2) BNS" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="law-tip">Helpful Tip (optional)</Label>
                <Input id="law-tip" value={form.tip} onChange={field('tip')}
                  placeholder="e.g. Always ask for a written receipt." />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rights-title">Rights Section Title</Label>
                <Input id="rights-title" value={form.rights_title} onChange={field('rights_title')} />
                <Label htmlFor="rights-points" className="text-xs text-muted-foreground">
                  Rights (one per line)
                </Label>
                <Textarea id="rights-points" rows={6} value={form.rights_points}
                  onChange={field('rights_points')}
                  placeholder={"Right 1\nRight 2\nRight 3"} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="proof-documents" className="text-violet-700 font-semibold">Proof / Documents to Gather</Label>
                <div className="h-[26px]" />
                <Label htmlFor="proof-documents" className="text-xs text-muted-foreground">
                  One document per line
                </Label>
                <Textarea id="proof-documents" rows={6} value={form.proof_documents}
                  onChange={field('proof_documents')}
                  placeholder={"Medical report / photos\nWitness names & contact\nComplaint copy"} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="punishment-title">Consequences Section Title</Label>
                <Input id="punishment-title" value={form.punishment_title} onChange={field('punishment_title')} />
                <Label htmlFor="punishment-points" className="text-xs text-muted-foreground">
                  Consequences (one per line)
                </Label>
                <Textarea id="punishment-points" rows={6} value={form.punishment_points}
                  onChange={field('punishment_points')}
                  placeholder={"Consequence 1\nConsequence 2"} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="support-title">Complaint / Help Section Title</Label>
                <Input id="support-title" value={form.support_title} onChange={field('support_title')} />
                <Label htmlFor="support-points" className="text-xs text-muted-foreground">
                  Steps / contacts (one per line)
                </Label>
                <Textarea id="support-points" rows={6} value={form.support_points}
                  onChange={field('support_points')}
                  placeholder={"Step 1 — File complaint at SP office\nStep 2 — Contact 100 helpline"} />
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving}
                className="bg-gradient-to-r from-amber-600 to-orange-500 hover:from-amber-700 hover:to-orange-600">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving…' : 'Save Law'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
